/**
 * Invitation-flow helpers — Phase 4 Stream I (AcceptInvitePage).
 *
 * All calls go to the same-origin BFF `/api/portal/invite/*`. These routes
 * are pre-session: the opaque token in the URL path is the credential.
 * No `portal_session` cookie is expected; no CSRF cookie is required on
 * the server (see api/src/routes/portal/index.ts), but `portalApi`
 * defensively echoes any `portal_csrf` cookie it finds anyway.
 */
import { portalApi } from './apiClient';
import type {
  PortalInvitationConsumeRequest,
  PortalInvitationLookup,
} from '@customer-portal/shared';

export interface InviteLookupResult {
  data: PortalInvitationLookup;
}

export interface InviteConsumeResult {
  portal_user_id: number;
  customer_id: number;
  email: string;
  magic_link_sent?: boolean;
}

export async function lookupInvitation(
  token: string,
  signal?: AbortSignal,
): Promise<InviteLookupResult> {
  // The path encodes the token verbatim; the BFF re-validates the shape so a
  // misencoded URL still 404s safely.
  const res = await portalApi<{ ok: true; data: PortalInvitationLookup }>(
    `/api/portal/invite/${encodeURIComponent(token)}`,
    { signal },
  );
  return { data: res.data };
}

export async function consumeInvitation(
  token: string,
  body: PortalInvitationConsumeRequest,
  signal?: AbortSignal,
): Promise<InviteConsumeResult> {
  const res = await portalApi<{ ok: true; data: InviteConsumeResult }>(
    `/api/portal/invite/${encodeURIComponent(token)}/consume`,
    {
      method: 'POST',
      body,
      signal,
    },
  );
  return res.data;
}
